import { ErrorMessage, TextOrHTML } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import type { FlattenedSectionProps } from "../";
import { Question } from "../Question";
import { isQuestion } from "../Question/types";
import {
  useAnswer,
  useQuestionnaire,
  useVisibilityMap,
} from "../../../../features/questionnaire";
import { prepareQuestionsForRender, type RenderItem, getErrors } from "./utils";
import { useCoverGenius } from "../../../../hooks/useCoverGenius";
import { isNoCoverValue } from "../Question/QuestionFactory/questions/PersonalisedCoverQuestion/constants";

type QuestionItemProps = RenderItem & {
  errors?: Record<string, string>;
  visibilityMap?: Record<string, boolean>;
};
/**
 * Render a question item (primary + optional children) with error display
 */
const QuestionItem = ({
  primary,
  children,
  errors,
  visibilityMap,
}: QuestionItemProps) => {
  const { hasError, errorMessage } = getErrors({
    primary,
    children,
    errors,
    visibilityMap,
  });

  const answer = useAnswer(primary.name);
  const isActive = answer !== undefined && !isNoCoverValue(answer);

  return (
    <>
      <div
        data-testid="mobius-section__personalised-cover-group"
        className={classNames("mobius-section__personalised-cover-group", {
          "--has-children": children.length > 0,
          "--is-active": isActive,
          "--is-invalid": hasError,
        })}
      >
        <Question {...primary} />
        {children.map(child => (
          <Question key={child.name} {...child} />
        ))}
      </div>
      {hasError && <ErrorMessage errorMessage={errorMessage} />}
    </>
  );
};

export const PersonalisedCoverSection = ({
  ref,
  ...props
}: FlattenedSectionProps) => {
  const { title, preamble, postamble, questions, className } = props;
  const classes = classNames("mobius", "mobius-section", className);
  const { groups = {}, errors = {} } = useQuestionnaire();
  const visibilityMap = useVisibilityMap();

  useCoverGenius({ questions });

  return (
    <section data-testid="mobius-section" ref={ref} className={classes}>
      {title && <h2 className="mobius-section__title">{title}</h2>}
      {preamble && (
        <TextOrHTML
          data-testid="mobius-section__preamble"
          className="mobius-section__preamble"
          text={preamble}
          textWrapper
        />
      )}
      <div className="mobius-section__question-container">
        {prepareQuestionsForRender({
          questions: questions.filter(isQuestion),
          groups,
          visibilityMap,
        }).map(item => (
          <QuestionItem
            key={item.primary.id}
            {...item}
            errors={errors}
            visibilityMap={visibilityMap}
          />
        ))}
      </div>
      {postamble && (
        <TextOrHTML
          data-testid="mobius-section__postamble"
          className="mobius-section__postamble"
          text={postamble}
          textWrapper
        />
      )}
    </section>
  );
};

PersonalisedCoverSection.displayName = "PersonalisedCoverSection";
